import React from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  useMediaQuery,
  Link as MuiLink,
  useTheme,
  Link,
  Chip,
  Stack,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import MapIcon from '@mui/icons-material/Map';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Link as RouterLink } from 'react-router-dom';
import Theme from '../../theme/Theme';

const PlaceCard: React.FC<EspacoDto> = ({
  id,
  fantasyName,
  address,
  description,
  logo,
  medias,
}) => {
  const imagens = medias.length > 2 ? medias : [logo];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        height: 'auto',
        minHeight: isMobile ? 'auto' : '220px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          transform: isMobile ? 'translateY(-2px)' : 'translateX(4px)',
        },
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Carrossel de imagens */}
      <Box
        sx={{
          position: 'relative',
          width: isMobile ? '100%' : '320px',
          height: isMobile ? '200px' : '220px',
          minWidth: isMobile ? 'auto' : '320px',
          overflow: 'hidden',
          bgcolor: '#f0f0f0',
          '& .swiper': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          },
          '& .swiper-pagination-bullet': {
            backgroundColor: '#ccc',
            opacity: 0.4,
          },
          '& .swiper-pagination-bullet-active': {
            backgroundColor: Theme.palette.primary.main,
            opacity: 1,
          },
          '& .swiper-button-next,.swiper-button-prev': {
            color: '#fff',
            opacity: 0.6,
            '&:after': {
              fontSize: '20px',
            },
          },
          '& .swiper-button-disabled': {
            opacity: 0,
          },
        }}
      >
        {imagens.length > 0 ? (
          <Swiper
            modules={[Pagination, Navigation]}
            cssMode={true}
            navigation={true}
            pagination={{ clickable: true }}
            spaceBetween={0}
            slidesPerView={1}
          >
            {imagens.map((img, idx) => (
              <SwiperSlide key={idx}>
                <CardMedia
                  component="img"
                  image={img?.url || ''}
                  alt={`slide-${idx}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#e0e0e0',
            }}
          >
            <ImageIcon sx={{ fontSize: 48, color: '#999' }} />
          </Box>
        )}
      </Box>

      {/* Conteúdo */}
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 0.75 : 1,
          p: isMobile ? 1.5 : 2,
          overflow: 'hidden',
          justifyContent: 'space-between',
        }}
      >
        {/* Título */}
        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          fontWeight="bold"
          sx={{
            color: 'primary.main',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.2,
            textAlign: 'left',
          }}
        >
          <MuiLink
            component={RouterLink}
            to={`/espaco/${id}`}
            underline="none"
            sx={{ color: 'inherit' }}
          >
            {fantasyName}
          </MuiLink>
        </Typography>

        {/* Endereço */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <LocationOnIcon
            sx={{ fontSize: isMobile ? 12 : 14, color: 'text.secondary', mt: 0.25, flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              fontSize: isMobile ? '0.7rem' : 'inherit',
              textAlign: 'left',
            }}
          >
            {address}
          </Typography>
        </Box>

        {/* Descrição */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            fontSize: isMobile ? '0.65rem' : '0.75rem',
            lineHeight: 1.3,
            textAlign: 'left',
          }}
        >
          {description?.slice(0, 600)}
          {description?.length > 600 ? '...' : ''}
        </Typography>

        {/* Footer com ações */}
        <Stack direction="row" spacing={isMobile ? 0.75 : 1} sx={{ pt: isMobile ? 0.5 : 1 }}>
          <IconButton
            href={`/espaco/${id}#media`}
            component={Link}
            size={isMobile ? 'small' : 'small'}
            sx={{
              flex: 1,
              bgcolor: 'primary.light',
              color: 'primary.main',
              borderRadius: 1,
              padding: isMobile ? '4px' : '6px',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
              },
            }}
          >
            <ImageIcon fontSize={isMobile ? 'inherit' : 'small'} />
          </IconButton>
          <IconButton
            href={`/espaco/${id}#map`}
            component={Link}
            size={isMobile ? 'small' : 'small'}
            sx={{
              flex: 1,
              bgcolor: 'primary.light',
              color: 'primary.main',
              borderRadius: 1,
              padding: isMobile ? '4px' : '6px',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
              },
            }}
          >
            <MapIcon fontSize={isMobile ? 'inherit' : 'small'} />
          </IconButton>
          <IconButton
            size={isMobile ? 'small' : 'small'}
            sx={{
              flex: 1,
              bgcolor: '#fff3e0',
              color: '#fdd835',
              borderRadius: 1,
              padding: isMobile ? '4px' : '6px',
              '&:hover': {
                bgcolor: '#fdd835',
                color: 'white',
              },
            }}
          >
            <StarIcon fontSize={isMobile ? 'inherit' : 'small'} />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PlaceCard;
